import React, { useCallback, useState } from "react";
import { BlockStack, Button, Collapsible, FormLayout, TextField } from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import type { IPurchaseOrder } from "../../../api_utils/types";
import { assertString } from "../../../helper_functions/utils";
import CustomDatePicker from "../../common/CustomDatePicker/CustomDatePicker";

type ImportDutiesAttributes = Pick<IPurchaseOrder, "cargo_control_number" | "entry_summary_id" | "release_date">;

export default function ImportDuties({
  purchaseOrder,
  onChange,
}: {
  readonly purchaseOrder: ImportDutiesAttributes;
  readonly onChange: (attributes: ImportDutiesAttributes) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const releaseDate = purchaseOrder.release_date ? new Date(`${purchaseOrder.release_date}T00:00:00`) : undefined;
  const handleToggle = useCallback((): void => {
    setOpen((previousOpen) => !previousOpen);
  }, []);

  const handleCargoControlNumberChange = useCallback(
    (cargoControlNumber: string): void => {
      onChange({ cargo_control_number: cargoControlNumber || null });
    },
    [onChange],
  );

  const handleEntrySummaryIdChange = useCallback(
    (entrySummaryId: string): void => {
      onChange({ entry_summary_id: entrySummaryId || null });
    },
    [onChange],
  );

  const handleReleaseDateChange = useCallback(
    (selectedDate: Date): void => {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      onChange({ release_date: `${year}-${month}-${day}` });
    },
    [onChange],
  );

  const handleReleaseDateClear = useCallback((): void => {
    onChange({ release_date: null });
  }, [onChange]);

  return (
    <BlockStack gap="400">
      <Button
        ariaControls="purchase-order-import-duties"
        ariaExpanded={open}
        icon={open ? ChevronUpIcon : ChevronDownIcon}
        onClick={handleToggle}
        textAlign="start"
        variant="plain"
      >
        Customs information
      </Button>
      <Collapsible id="purchase-order-import-duties" open={open}>
        <FormLayout>
          <FormLayout.Group>
            <TextField
              autoComplete="off"
              label="Cargo control number"
              onChange={handleCargoControlNumberChange}
              value={assertString(purchaseOrder.cargo_control_number)}
            />
            <TextField
              autoComplete="off"
              label="Entry summary ID"
              onChange={handleEntrySummaryIdChange}
              value={assertString(purchaseOrder.entry_summary_id)}
            />
            <CustomDatePicker
              date={releaseDate}
              label="Release date"
              onClear={handleReleaseDateClear}
              placeholder="Select release date"
              setDate={handleReleaseDateChange}
            />
          </FormLayout.Group>
        </FormLayout>
      </Collapsible>
    </BlockStack>
  );
}
